/// <amd-module name="VSSUI/Components/Tabs/TabBar" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { ITabBarProps, ITabProviderContext, IVssContributedTab } from "VSSUI/Components/Tabs/Tabs.Props";
import { Observer } from "VSSUI/Observer";
import { SurfaceBackground, SurfaceContext } from "VSSUI/Surface";
import { css } from "VSSUI/Util";
import { Tab } from "./Tab";
import { TabProviderContext } from "./TabProviderContext";
import { Tabs } from "./Tabs";

/**
 * Renders tabs (provided as children or through a tab provider context) as well as addtional
 * content such as in-line filters, view options, etc.
 *
 * Tabs provided directly as children will always be place before contributed tabs.
 */
export class TabBar extends React.Component<ITabBarProps> {
    public static contextType = ScreenContext;

    public render() {
        return (
            <Observer size={this.context.size}>
                {(props: { size: ScreenSize }) => {
                    //determine on which screen sizes we should stack line by line.
                    const shouldBeStacked = props.size <= ScreenSize.medium;
                    return (
                        <SurfaceContext.Consumer>
                            {surfaceContext => (
                                <TabProviderContext.Consumer>
                                    {(tabProviderContext: ITabProviderContext) => {
                                        const selectedTabId = this.props.selectedTabId || tabProviderContext.selectedId;
                                        return (
                                            <div
                                                className={css(
                                                    this.props.className,
                                                    "bolt-tabbar flex-row flex-center flex-noshrink",
                                                    !this.props.disableSticky && props.size >= ScreenSize.small && "sticky",
                                                    surfaceContext.background === SurfaceBackground.neutral && "bolt-tabbar-grey",
                                                    surfaceContext.background === SurfaceBackground.callout && "bolt-tabbar-on-callout"
                                                )}
                                            >
                                                <Tabs
                                                    selectedTabId={selectedTabId}
                                                    {...this.props}
                                                    className={css(
                                                        this.props.tabsClassName,
                                                        "bolt-tabbar-tabs flex-grow",
                                                        !shouldBeStacked && "flex-noshrink",
                                                        shouldBeStacked && "flex-wrap"
                                                    )}
                                                >
                                                    {this.props.children}
                                                    {tabProviderContext.tabs.map(this.createTab)}
                                                </Tabs>
                                                {this.renderAdditionalContent(tabProviderContext.renderFilterBar)}
                                            </div>
                                        );
                                    }}
                                </TabProviderContext.Consumer>
                            )}
                        </SurfaceContext.Consumer>
                    );
                }}
            </Observer>
        );
    }

    private renderAdditionalContent = (renderFilterBar: ((inTabBar: boolean) => JSX.Element | undefined) | undefined) => {
        if (renderFilterBar) {
            return renderFilterBar(true);
        }
        return this.props.renderAdditionalContent && this.props.renderAdditionalContent();
    };

    private createTab = (tab: IVssContributedTab): JSX.Element => {
        return (
            <Tab
                ariaLabel={tab.ariaLabel}
                badgeCount={tab.badgeCount}
                className={tab.className}
                key={tab.id}
                iconProps={tab.iconProps}
                id={tab.id}
                name={tab.name}
                onBeforeTabChange={tab.onBeforeTabChange}
                url={tab.url && ObservableLike.getValue(tab.url)}
                renderBadge={tab.renderBadge}
            />
        );
    };
}
